// Generates platform-appropriate mock assets for a freshly-completed agent run.
// This is still deterministic prototype data, but it should honor the user's
// prompt enough for the Create flow to feel believable during MVP testing.

import { generateCoverImage } from "./coverImageGenerator";

function clean(value = "") {
  return String(value || "").trim();
}

function compactWhitespace(value = "") {
  return clean(value).replace(/\s+/g, " ");
}

function sentenceCase(value = "") {
  const text = clean(value);
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function includesAny(value = "", terms = []) {
  const lower = value.toLowerCase();
  return terms.some((term) => lower.includes(term));
}

function getRunPrompt(run = {}) {
  return compactWhitespace(run.prompt || run.title || "Create a practical social post about AI workflows.");
}

function trimTitle(value = "", length = 60) {
  const text = compactWhitespace(value);
  return text.length > length ? text.slice(0, length).trim() : text;
}

function inferTopicHook(run = {}) {
  const prompt = getRunPrompt(run);
  const stripped = prompt
    .replace(/^create\s+(a|an|the)?\s*/i, "")
    .replace(/^write\s+(a|an|the)?\s*/i, "")
    .replace(/^draft\s+(a|an|the)?\s*/i, "");
  return sentenceCase(stripped.split(/[.!?]/)[0].slice(0, 120)) || "A practical operator note on AI workflows";
}

function makeTextLength(...parts) {
  return parts
    .flat()
    .filter((part) => clean(part) && clean(part) !== "-")
    .join("\n\n").length;
}

function maxSegmentLength(segments = []) {
  return Math.max(0, ...segments.filter(Boolean).map((segment) => clean(segment).length));
}

function buildBrief(run = {}) {
  const prompt = getRunPrompt(run);
  const lower = prompt.toLowerCase();
  const audience = clean(run.audience) || "operators building practical AI workflows";
  const wantsDad = includesAny(lower, ["dad", "father", "parent"]);
  const wantsSoftCta = includesAny(lower, ["soft cta", "workflow audit", "ask about"]);

  if (includesAny(lower, ["intake", "follow-up", "follow up", "small business", "smb", "ai guru", "workflow audit"])) {
    return {
      topic: "practical AI agents for intake and follow-up",
      hook: "Small-business AI should feel practical before it feels impressive.",
      linkedinParagraphs: [
        "A lot of owners do not need an AI guru pitch. They need fewer missed calls, faster follow-up, and a cleaner path from first question to booked job.",
        `For ${audience}, the useful starting point is an agent that captures the request, asks the next qualifying question, summarizes the context, and nudges the next step.`,
        wantsDad
          ? "As a dad building practical systems, my bar is simple: does this save attention when real life is already loud?"
          : "The bar is simple: does this save attention when the day is already stretched?",
        "Start with one workflow: intake, follow-up, quote reminders, or missed-call replies. Make it boring, useful, and measurable before making it fancy.",
      ],
      cta: wantsSoftCta
        ? "If you want the practical version for your business, ask me for a workflow audit."
        : "If this is on your radar, start with the one follow-up step your team drops most often.",
      xHook: "AI for small business should start with intake and follow-up, not hype.",
      xPosts: [
        "1/ The first useful agent captures the request, asks the next qualifying question, and summarizes what happened.",
        "2/ The next useful move is follow-up: missed-call replies, quote nudges, reminders, and simple next steps.",
        wantsDad
          ? "3/ As a dad, I care less about flashy demos and more about systems that save attention after hours."
          : "3/ The win is not sounding futuristic. The win is fewer dropped balls and a workflow the owner trusts.",
        "4/ Pick one messy handoff. Automate that. Measure the time saved. Then expand.",
      ],
      xCta: "Want the practical version? Ask me for a workflow audit.",
      hashtags: ["#AI", "#SmallBusiness", "#Automation"],
      researchInsight: "Practical owners respond to concrete workflow relief: fewer missed leads, faster replies, and less manual follow-up.",
      imagePrompt: "Editorial SaaS-style visual of a small business intake pipeline: incoming message, qualification step, follow-up reminder, and booked appointment. Clean dark interface, cyan highlights, no fake dashboard text.",
    };
  }

  if (includesAny(lower, ["hvac", "plumb", "electrician", "contractor", "voice agent", "service business"])) {
    return {
      topic: "AI voice agents for service businesses",
      hook: "The easiest AI win for service businesses is not content. It is answering the phone better.",
      linkedinParagraphs: [
        "HVAC, plumbing, and home-service teams lose money in the gaps: after-hours calls, slow qualification, missed follow-up, and thin handoffs.",
        `For ${audience}, a voice agent should do the simple work first: answer, qualify, summarize, and route the next step.`,
        "That is not a replacement for the business. It is a cleaner front door for customers who already wanted help.",
      ],
      cta: "If you want to map the first workflow, ask me about a voice-agent audit.",
      xHook: "Voice agents make sense when they solve the boring service-business gaps.",
      xPosts: [
        "1/ Start with missed calls and after-hours intake.",
        "2/ Ask the same qualifying questions your best dispatcher already asks.",
        "3/ Summarize the call so the human can move faster.",
        "4/ Measure booked jobs, response time, and follow-up completion.",
      ],
      xCta: "That is the AI use case I would test first.",
      hashtags: ["#AI", "#ServiceBusiness", "#Automation"],
      researchInsight: "Service businesses need AI framed around response speed, qualification, and revenue leakage rather than novelty.",
      imagePrompt: "Clean visual of a service-business call workflow: phone intake, qualification notes, dispatch handoff, and follow-up reminder. Professional, modern, no hype.",
    };
  }

  if (includesAny(lower, ["skill", "phase-aware", "phase aware", "codex", "agent workflow", "operator"])) {
    return {
      topic: "phase-aware skills for AI operators",
      hook: "Most teams are still tuning prompts. The stronger move is building skills.",
      linkedinParagraphs: [
        "A prompt is a one-off ask. A skill is a reusable capability with inputs, outputs, boundaries, and a phase where it belongs.",
        `For ${audience}, the unlock is routing by phase: research, planning, generation, critique, and formatting.`,
        "That is how smaller models produce sharper work: the agent is not guessing what to do next.",
      ],
      cta: "If you are building agentic systems, start by naming the five skills you reuse every week.",
      xHook: "Stop tuning prompts. Start writing reusable skills.",
      xPosts: [
        "1/ A skill is a reusable instruction with a contract: inputs, outputs, boundaries, and when to use it.",
        "2/ Phase matters. A research skill should not behave like a critique skill.",
        "3/ My default phases: research, planning, generation, critique, formatting.",
        "4/ Better routing often beats a bigger model.",
      ],
      xCta: "Build the skill library before chasing another prompt trick.",
      hashtags: ["#AI", "#Agents", "#Operators"],
      researchInsight: "The prompt-versus-skill framing resonates with operators who are scaling repeatable agent workflows.",
      imagePrompt: "Dark studio editorial illustration of modular AI workflow blocks labeled research, plan, generate, critique, and format. Premium SaaS style, no people.",
    };
  }

  if (includesAny(lower, ["content system", "repurpose", "blog", "content stack", "media"])) {
    return {
      topic: "AI content systems for operators",
      hook: "A content system should turn one strong idea into a week of useful posts.",
      linkedinParagraphs: [
        "The hard part is not generating more words. It is keeping the idea, voice, and purpose intact across every endpoint.",
        `For ${audience}, the workflow is simple: capture the idea, create the long-form source, cut it into platform-native posts, then review before scheduling.`,
        "That is how content becomes an operating rhythm instead of another blank page.",
      ],
      cta: "If you are building this, start with one source idea and five platform cuts.",
      xHook: "The best content engine starts with one source idea.",
      xPosts: [
        "1/ Capture the idea.",
        "2/ Write the long-form source.",
        "3/ Cut platform-native versions.",
        "4/ Review against the actual platform preview.",
        "5/ Schedule only after approval.",
      ],
      xCta: "That is the workflow I would automate first.",
      hashtags: ["#ContentSystems", "#AI", "#Workflow"],
      researchInsight: "Operators need content workflows that preserve voice and approval control while reducing repeated manual formatting.",
      imagePrompt: "Modern content operating board showing one source idea branching into LinkedIn, X, Instagram, and blog assets. Clean interface, energetic but professional.",
    };
  }

  const topic = inferTopicHook(run);
  return {
    topic,
    hook: `${topic}.`,
    linkedinParagraphs: [
      "The useful version is usually more practical than the headline makes it sound.",
      `For ${audience}, the question is not whether AI is impressive. The question is where it removes friction from a real workflow.`,
      "Start with the repeated handoff, the missed follow-up, or the review step that slows everything down. Then make that one part cleaner.",
    ],
    cta: "If you are exploring this, map the workflow before picking the tool.",
    xHook: topic.slice(0, 180),
    xPosts: [
      "1/ Start with the workflow, not the tool.",
      "2/ Find the repeated handoff or missed follow-up.",
      "3/ Add AI where it saves attention and improves consistency.",
      "4/ Keep a human review gate before anything public ships.",
    ],
    xCta: "That is the practical path.",
    hashtags: ["#AI", "#Workflow", "#Automation"],
    researchInsight: "The prompt points toward a practical workflow story rather than a generic AI announcement.",
    imagePrompt: "Clean editorial visual of an AI workflow moving from raw idea to reviewed output. Dark interface, blue and cyan highlights, professional product feel.",
  };
}

function buildLinkedIn(run, idx, cover) {
  const brief = buildBrief(run);
  const hook = brief.hook;
  const body = brief.linkedinParagraphs.join("\n\n");
  const cta = brief.cta;

  return {
    id: `asset_${run.id}_li_${idx}`,
    run_id: run.id,
    project_id: run.project_id,
    title: `LinkedIn - ${trimTitle(run.title || brief.topic)}`,
    platform: "linkedin",
    asset_type: "social_post",
    status: "needs_review",
    skill_used: "AdamVoice",
    hook,
    body,
    cta,
    hashtags: brief.hashtags,
    char_count: makeTextLength(hook, body, cta, brief.hashtags.join(" ")),
    char_limit: 3000,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildXThread(run, idx, cover) {
  const brief = buildBrief(run);
  const hook = brief.xHook;
  const body = brief.xPosts.join("\n\n");
  const cta = brief.xCta;
  const segments = [hook, ...brief.xPosts, cta];

  return {
    id: `asset_${run.id}_x_${idx}`,
    run_id: run.id,
    project_id: run.project_id,
    title: `X Thread - ${trimTitle(run.title || brief.topic)}`,
    platform: "x",
    asset_type: "thread",
    status: "needs_review",
    skill_used: "AdamVoice",
    hook,
    body,
    cta,
    hashtags: brief.hashtags.filter((tag) => tag !== "#SmallBusiness").slice(0, 2),
    char_count: maxSegmentLength(segments),
    thread_total_chars: makeTextLength(segments),
    char_limit: 280,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildShorts(run, idx) {
  const brief = buildBrief(run);
  const body = [
    `[0:00-0:04] HOOK: "${brief.xHook}"`,
    `[0:04-0:16] PROBLEM: ${brief.linkedinParagraphs[0]}`,
    `[0:16-0:30] SHIFT: Show the practical workflow, not a generic AI pitch.`,
    `[0:30-0:42] PROOF: ${brief.linkedinParagraphs[1]}`,
    `[0:42-0:45] CTA: "${brief.xCta}"`,
  ].join("\n");

  return {
    id: `asset_${run.id}_yts_${idx}`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Shorts - ${trimTitle(run.title || brief.topic, 56)} (45s)`,
    platform: "youtube_shorts",
    asset_type: "video_script",
    status: "needs_review",
    skill_used: "AdamVoice",
    hook: brief.xHook,
    body,
    cta: brief.xCta,
    hashtags: ["#AI", "#shorts", "#workflow"],
    char_count: makeTextLength(brief.xHook, body, brief.xCta),
    char_limit: 5000,
    cover_image: null,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildInstagram(run, idx, cover) {
  const brief = buildBrief(run);
  const body = [
    `Slide 1 - ${brief.hook}`,
    `Slide 2 - ${brief.linkedinParagraphs[0]}`,
    `Slide 3 - ${brief.linkedinParagraphs[1]}`,
    "Slide 4 - Start with one repeatable workflow.",
    "Slide 5 - Keep the human approval gate.",
    `Slide 6 - ${brief.cta}`,
  ].join("\n");

  return {
    id: `asset_${run.id}_ig_${idx}`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Instagram - ${trimTitle(run.title || brief.topic, 56)}`,
    platform: "instagram",
    asset_type: "carousel",
    status: "needs_review",
    skill_used: "AdamVoice",
    hook: brief.hook,
    body,
    cta: brief.cta,
    hashtags: brief.hashtags,
    char_count: makeTextLength(brief.hook, body, brief.cta),
    char_limit: 2200,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildThreads(run, idx, cover) {
  const brief = buildBrief(run);
  const body = `${brief.linkedinParagraphs[0]}\n\n${brief.linkedinParagraphs[1]}`;

  return {
    id: `asset_${run.id}_th_${idx}`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Threads - ${trimTitle(run.title || brief.topic, 56)}`,
    platform: "threads",
    asset_type: "social_post",
    status: "needs_review",
    skill_used: "AdamVoice",
    hook: brief.xHook,
    body,
    cta: brief.xCta,
    hashtags: brief.hashtags.slice(0, 2),
    char_count: makeTextLength(brief.xHook, body, brief.xCta),
    char_limit: 500,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildFacebook(run, idx, cover) {
  const brief = buildBrief(run);
  const body = brief.linkedinParagraphs.join("\n\n");

  return {
    id: `asset_${run.id}_fb_${idx}`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Facebook - ${trimTitle(run.title || brief.topic, 56)}`,
    platform: "facebook",
    asset_type: "social_post",
    status: "needs_review",
    skill_used: "AdamVoice",
    hook: `A practical note for ${run.audience || "operators"}:`,
    body,
    cta: brief.cta,
    hashtags: brief.hashtags.slice(0, 2),
    char_count: makeTextLength(body, brief.cta),
    char_limit: 5000,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildBluesky(run, idx, cover) {
  const brief = buildBrief(run);
  const body = `${brief.xHook}\n\n${brief.xPosts[0] || brief.linkedinParagraphs[0]}`;

  return {
    id: `asset_${run.id}_bs_${idx}`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Bluesky - ${trimTitle(run.title || brief.topic, 56)}`,
    platform: "bluesky",
    asset_type: "social_post",
    status: "needs_review",
    skill_used: "AdamVoice",
    hook: brief.xHook,
    body,
    cta: "-",
    hashtags: brief.hashtags.slice(0, 1),
    char_count: makeTextLength(body),
    char_limit: 300,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildResearchBrief(run) {
  const brief = buildBrief(run);
  const prompt = getRunPrompt(run);
  const body = [
    `Prompt: ${prompt}`,
    `Audience: ${run.audience || "Operators"}`,
    `Angle: ${brief.topic}`,
    `Key insight: ${brief.researchInsight}`,
    "Risk check: avoid generic AI hype; keep the proof tied to a specific workflow and require approval before publishing.",
  ].join("\n");

  return {
    id: `asset_${run.id}_rb`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Research notes - ${trimTitle(run.title || brief.topic)}`,
    platform: "n/a",
    asset_type: "research_brief",
    status: "supporting",
    skill_used: "ResearchBrief",
    hook: "Agent research notes",
    body,
    cta: "-",
    hashtags: [],
    char_count: body.length,
    char_limit: 5000,
    cover_image: null,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildImagePrompt(run, cover) {
  const brief = buildBrief(run);
  const body = `${brief.imagePrompt} 16:9 ratio. Premium social content visual. No fake text or distorted UI.`;

  return {
    id: `asset_${run.id}_img`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Cover image prompt - ${trimTitle(run.title || brief.topic)}`,
    platform: "shared",
    asset_type: "image_prompt",
    status: "supporting",
    skill_used: "BrandImagePrompt",
    hook: "-",
    body,
    cta: "-",
    hashtags: [],
    char_count: body.length,
    char_limit: 1500,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

function buildUploadedCoverEntry(run, cover) {
  return {
    id: `asset_${run.id}_img`,
    run_id: run.id,
    project_id: run.project_id,
    title: `Uploaded cover - ${trimTitle(run.title || "Create run")}`,
    platform: "shared",
    asset_type: "image_prompt",
    status: "supporting",
    skill_used: "UserUpload",
    hook: "-",
    body: "Your uploaded cover image. Applied to every visual platform and auto-resized per platform's preferred dimensions.",
    cta: "-",
    hashtags: [],
    char_count: 0,
    char_limit: 0,
    cover_image: cover,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  };
}

const BUILDERS = {
  linkedin: buildLinkedIn,
  x: buildXThread,
  youtube_shorts: buildShorts,
  instagram: buildInstagram,
  threads: buildThreads,
  facebook: buildFacebook,
  bluesky: buildBluesky,
};

export function generateRunAssets(run) {
  const platforms = run?.platforms || [];
  const mode = run?.image_mode || "generate";
  const brief = buildBrief(run);
  let cover = null;

  if (mode === "upload" && run?.uploaded_image) {
    cover = run.uploaded_image;
  } else if (mode === "generate") {
    cover = generateCoverImage({
      title: brief.topic,
      subtitle: run?.audience ? `For ${run.audience}` : "",
      seed: run?.id || run?.title || brief.topic,
    });
  }

  const deliverables = platforms
    .map((platform, idx) => (BUILDERS[platform] ? BUILDERS[platform](run, idx, cover) : null))
    .filter(Boolean);
  const supporting = [buildResearchBrief(run)];

  if (mode === "generate") supporting.push(buildImagePrompt(run, cover));
  if (mode === "upload" && cover) supporting.push(buildUploadedCoverEntry(run, cover));

  return [...deliverables, ...supporting];
}
